import React from 'react';
import {Box, Tab, Tabs, Text, Heading, Paragraph, Anchor, Button} from 'grommet';
import {HelpOption} from "grommet-icons";

export default function AboutHolder(props) {
    const [idx, setIdx] = React.useState(0);
    return (
        <Box
            direction="column"
            justify="center"
            fill
        >
            <Box
                direction="column"
                justify="start"
                pad="medium"
                margin="large"
                elevation="medium"
                round="small"
                overflow="scroll"
                height="90%"
                // flex="grow"
                // alignSelf="stretch"
            >
                <Box
                    flex
                    // direction="col"
                    overflow="scroll"
                >
                    <Text size="large" weight="bold">About & User Manual</Text>
                    <Tabs
                        // flex="grow"
                        activeIndex={idx}
                        onActive={(i) => setIdx(i)}
                    >
                        <Tab title={<Box><Text size="large">About This Project</Text></Box>}>
                            <Box pad="medium" background="light-2" round="small">
                                <Heading level={4}>About this project</Heading>
                                <Paragraph fill size="small">
                                    As developing countries emerge from the COVID-19 crisis, they will need to pivot
                                    rapidly to relaunch their economies. Given the severity of the current economic
                                    downturn, the world has an opportunity to pivot to greener and more inclusive forms
                                    of economic growth in the next business cycle.
                                </Paragraph>
                                <Paragraph fill size="small">
                                    Standing in the way is a second crisis: sovereign debt. Governments cannot enact the
                                    climate-friendly growth commitments enshrined by their Nationally Determined
                                    Contributions, in accordance with the Paris Agreement, nor their commitments to
                                    reverse global biodiversity loss by 2030, while simultaneously facing serious debt
                                    distress.
                                </Paragraph>
                                <Paragraph fill size="small">
                                    China is in a unique position to support countries in addressing both of these
                                    crises at once. As the world’s largest bilateral creditor, it can consider a variety
                                    of sustainable alternatives for debt relief for the most debt-stressed countries,
                                    including debt-for-nature swaps, debt-for-climate swaps, and sustainability-linked
                                    bond issuances.
                                </Paragraph>
                                <Paragraph fill size="small">
                                    This project explores opportunities for China to alleviate debt burdens in exchange
                                    for debtor nation commitments to climate change mitigation and/or adaptation and
                                    environmental protection through “debt-for-climate” and “debt-for-nature” swaps.
                                </Paragraph>
                                <Heading level={5}>What debt swaps have to offer</Heading>

                                <Paragraph fill size="small">
                                    Traditional ‘debt-for-nature swaps’ are an incentive-based solution for achieving
                                    conservation targets in highly indebted countries, where organizations and/or
                                    government creditors negotiate with government debtors to cancel or reduce debts in
                                    return for binding commitments to protect threatened species, reduce deforestation,
                                    or achieve other environmental goals, often through the creation of protected areas.
                                    Historically, these swaps have typically been led by large organizations, like The
                                    Nature Conservancy (TNC) or World Wildlife Fund (WWF), which targeted relatively
                                    modest debts to increase protection in nationally significant hotspots of
                                    conservation concern.
                                </Paragraph>
                                <Paragraph fill size="small">
                                    The design of debt swaps has evolved to increase investments over longer periods of
                                    time, include more involvement of a diverse group of stakeholders, and ramp up
                                    enforcement. This includes the extension of debt relief incentives to facilitate
                                    national climate mitigation and adaptation targets in debtor countries, termed
                                    ‘debt-for-climate’ swaps.
                                </Paragraph>
                                <Paragraph fill size="small">
                                    While still in their infancy, debt-for-climate swaps appear to have great
                                    potential—both on their own and in combination with debt-for-nature swaps. Most
                                    recently, the Seychelles implemented the first marine debt swap for dual nature and
                                    climate benefits, expanding marine protected areas to cover nearly one-third of the
                                    country’s seascape in exchange for relief of $21.6 million in sovereign debt.
                                </Paragraph>
                                <Paragraph fill size="small">
                                    The debt swap model has also propelled new frontiers of sustainability-linked debt
                                    restructuring, including green bonds, which are linked to particular local
                                    conservation, emissions-reduction, or climate adaptation goals. Globally, the green
                                    bond market is growing quickly and is projected to reach $1 trillion in 2021. Green
                                    bond certification schemes vary widely in their definitions, with broad agreement
                                    around the Green Bond Principles regarding how funds are used, how projects are
                                    selected, how proceeds are managed, and how performance is reported transparently.
                                    Among newly emerging options for sovereign debt are “nature performance bonds,”
                                    which incentivize measurable progress toward national conservation goals.
                                </Paragraph>

                                <Heading level={5}>Methodology</Heading>

                                <Paragraph fill size="small">
                                    While most countries could benefit from Chinese debt-for-nature or debt-for-climate
                                    swaps, priorities need to be identified to ensure China’s investments yield the
                                    maximum potential benefits. Crucial to this prioritization is the consideration of
                                    need and likelihood of success. Our analysis incorporates these factors into the
                                    assessment of countries’ debt swap potential based upon three key characteristics:
                                </Paragraph>

                                <Heading level={4}>1. Where is Chinese debt relief needed?</Heading>

                                <Paragraph fill size="small">
                                    In total, 102 low- and middle-income countries were considered in the analysis, with
                                    41 countries prioritized with the greatest potential need for Chinese debt relief.
                                </Paragraph>
                                <Paragraph fill size="small">
                                    Of 87 low- and middle-income countries receiving development loans from China’s two
                                    main policy banks—China Development Bank and Export-Import Bank of China—since 2008,
                                    25 countries whose loans exceeded 4.80 percent of their GDP (median) were
                                    prioritized, as well as countries whose loans from all external debts exceed 38.47
                                    percent of their GDP (median).
                                </Paragraph>
                                <Paragraph fill size="small">
                                    Also prioritized are 11 additional countries participating in the Debt Service
                                    Suspension Initiative (DSSI), whose payments due to China in 2020 constituted more
                                    than 51.30 percent of all bilateral payments due in 2020, and more than 13.17
                                    percent of all external payments due in 2020. These thresholds represent the median
                                    of 62 DSSI countries with ongoing payments to China.
                                </Paragraph>

                                <Heading level={4}>2. Where are the environmental threats?</Heading>

                                <Paragraph fill size="small">
                                    To assess differential benefits of debt-for-nature and debt-for-climate swaps across
                                    countries, countries were scored according to separate climate and biodiversity
                                    threats based upon how they compare to the global median of all 102 low- and
                                    middle-income countries.
                                </Paragraph>

                                <Paragraph fill size="small">
                                    For climate threats, country-level contributions to climate change (annual carbon
                                    emissions) and overall vulnerability to the impacts of climate change (Climate
                                    Vulnerability Index) were considered. Countries with an annual emissions rate
                                    greater than 6.08 Mt CO2 per year and a Climate Vulnerability Index greater than
                                    0.478 received the highest climate threat score; countries exceeding one of these
                                    thresholds received a moderate threat score, and countries below both thresholds
                                    received a low threat score.
                                </Paragraph>
                                <Paragraph fill size="small">
                                    For biodiversity threats, each country’s annual rate of tree cover loss (as a
                                    percent of available forest in 2010) and the density of threatened species (mammals,
                                    amphibians, reptiles, and birds) were considered. Countries with an annual tree
                                    cover loss greater than 0.357% per year and a threatened species density greater
                                    than 0.00026 spp. per km2 received the highest biodiversity threat score.
                                </Paragraph>
                                <Heading level={4}>3. Where are implementation opportunities greatest?</Heading>
                                <Paragraph fill size="small">
                                    The presence of climate and biodiversity threats doesn’t necessarily mean
                                    implementing a debt swap will be successful—this may be because the government has
                                    few or vague existing commitments to climate mitigation, or there is simply a lack
                                    of ecologically valuable land remaining that can be protected (among other potential
                                    barriers to success).
                                </Paragraph>
                                <Paragraph fill size="small">
                                    To account for the opportunity for debt swap success, we considered the costs
                                    associated with achieving each country’s nationally determined contributions (NDCs)
                                    for renewable energy under the Paris Climate Agreement, as well as the proportion of
                                    countries’ remaining intact landscapes that are not currently covered by protected
                                    areas. Countries whose NDC commitments exceed 5.50 percent of their GDP (median)
                                    were given a high score for debt-for-climate opportunity, and countries with more
                                    than 78.39 percent of their intact lands unprotected (median) were given a high
                                    score for debt-for-nature opportunity.
                                </Paragraph>
                                <Paragraph fill size="small">
                                    The overall potential for debt-for-climate and debt-for-nature swaps in each country
                                    with high Chinese debt exposure was calculated as the sum of the respective scores
                                    across environmental threats and debt swap opportunities.
                                </Paragraph>
                                <Heading level={4}>To learn more about this project</Heading>
                                <Paragraph fill size="small">
                                    <Anchor
                                        href="https://science.sciencemag.org/content/371/6528/468"
                                        target="_blank"
                                        label={<Text color="blue" size="small"><u>Read the full article in
                                            Science</u></Text>}/>.
                                    <Anchor
                                        href="https://www.bu.edu/gdp/2021/01/29/debt-swaps-how-china-can-create-opportunities-for-financial-and-environmental-stability/"
                                        target="_blank"
                                        label={<Text color="blue" size="small"><u>Read the Summary Blog</u></Text>}/>.
                                </Paragraph>

                            </Box>
                        </Tab>
                        <Tab
                            title={<Box><Text size="large">How to Use this Site</Text></Box>}
                            icon={<HelpOption/>} reverse={true}>
                            <Box pad="medium" background="light-2" round="small">
                                <Heading level={4}>How to use this site</Heading>
                                <Paragraph fill size="small">
                                    This site was developed to enhance users’ understanding of the links between Chinese
                                    debt exposure, environmental threats, and debt swap opportunities outlined in the
                                    Science <Anchor href="https://science.sciencemag.org/content/371/6528/468"
                                                    target="_blank"
                                                    label={<Text color="blue" size="small"><u>article</u></Text>}/>,
                                    “China can help solve the debt and environmental crises.” It is designed for you to
                                    explore the relationships between various financial and environmental variables that
                                    can help prioritize countries where debt-for-nature and/or debt-for-climate swaps
                                    show promise for maximizing Chinese debt relief and mitigating environmental threats
                                    in debtor countries.
                                </Paragraph>
                                <Heading level={4}>Global Outlook</Heading>
                                <Paragraph fill size="small">
                                    This site was developed to enhance users’ understanding of the links between Chinese
                                    debt exposure, environmental threats, and debt swap opportunities outlined in the
                                    Science article, “China can help solve the debt and environmental crises.” It is
                                    designed for you to explore the relationships between various financial and
                                    environmental variables that can help prioritize countries where debt-for-nature
                                    and/or debt-for-climate swaps show promise for maximizing Chinese debt relief and
                                    mitigating environmental threats in debtor countries.
                                </Paragraph>
                                <Paragraph fill size="small">
                                    On the <Anchor onClick={() => props.changeModule("outlook")}
                                                   label={<Text color="blue" size="small"><u>Global
                                                       Outlook</u></Text>}/> page, you can view the results of our
                                    initial exploratory analysis into the potential for debt-for-nature and
                                    debt-for-climate swaps in countries under greater Chinese debt stress.
                                </Paragraph>
                                <Paragraph fill size="small">
                                    Hovering over different countries will show you the characteristics defining our
                                    assessment of potential debt swaps.
                                    <ul>
                                        <li>You can see whether the country was considered under relatively ‘high’
                                            Chinese debt exposure, including the country’s total amount of Chinese
                                            finance
                                            commitments since 2008 and—if the country is included in the Debt Service
                                            Suspension Initiative (DSSI)—the total amount of payments that were
                                            originally due to China in 2020.
                                        </li>
                                        <li>Countries considered under relatively ‘low’ Chinese debt exposure were not
                                            assessed in our analysis, but you can still view the China debt profile for
                                            these countries.
                                        </li>
                                        <li>You can also see the country’s expected potential for debt-for-nature and/or
                                            debt-for-climate swaps, including the key environmental characteristics used
                                            in our assessment.
                                        </li>
                                    </ul>
                                </Paragraph>
                                <Box direction="row" justify="center">
                                    <Button onClick={() => props.changeModule("outlook")} label="Open Outlook Module"/>
                                </Box>
                                <Heading level={4}>Explore</Heading>
                                <Paragraph fill size="small">
                                    There are three pages you can visit to explore the data underlying countries’ China
                                    Debt Exposure, Environmental Threats, and Opportunities. For each page, you can
                                    generate maps and graphs of the financial and environmental data included in our
                                    analysis.
                                </Paragraph>
                                <Paragraph fill size="small">
                                    To generate maps, select the variable you would like to map from the drop-down list,
                                    and the map will automatically update. To generate scatterplots, select the
                                    variables you would like to graph on the x- and y-axes from the drop-down list, and
                                    the graph will automatically update. You can hover the cursor over individual points
                                    in the graph to see the name of the country represented by the data point, which
                                    will also highlight the country on the map.
                                </Paragraph>
                                <Paragraph fill size="small">
                                    The colors in the map correspond to quartiles based on the selected variable,
                                    distinguished as low, moderately low, moderately high, and high. The colors in the
                                    graphs distinguish between countries considered as having relatively ‘high’ debt
                                    exposure and countries considered as having lower debt exposure. The graphs also
                                    highlight the median of each variable, which allows you to assess each countries
                                    status relative to all 102 countries considered.
                                </Paragraph>
                                <Heading level={5}><u>China Debt Exposure</u></Heading>
                                <Paragraph fill size="small">
                                    This page includes financial data used to identify countries under greater Chinese
                                    debt exposure.
                                    In addition to individual variables, you can choose the following maps to generate
                                    the figures included in our original publication:
                                    <ul>
                                        <li>
                                            Debt Exposure 1: This map shows countries characterized at various levels of
                                            potential debt stress, based on the relationship between China finance
                                            commitments
                                            and all current external debts. Countries with high China debt exposure were
                                            included
                                            in our debt swap assessments.
                                        </li>
                                        <li>
                                            Debt Exposure 2: This map shows DSSI countries characterized at various
                                            levels
                                            of
                                            potential debt stress, based on the relationship between China’s dominance
                                            within
                                            bilateral and all external payments due in 2020. Countries with high China
                                            dominance
                                            in both bilateral and external payments were included in our debt swap
                                            assessments.
                                        </li>
                                    </ul>
                                </Paragraph>
                                <Paragraph fill size="small">
                                    You can generate the same scatterplots in our original publication by selecting the
                                    following for both the x- and y-axes:
                                    <ul>
                                        <li>Debt Stress 1: This graph shows the thresholds used to define countries
                                            under greater potential Chinese debt stress according to China finance
                                            commitments and external debt. The thresholds (dotted lines) indicate the
                                            median of all countries. This graph aligns directly with the Debt Stress 1
                                            map.
                                        </li>
                                        <li>Debt Stress 2: This graph shows the thresholds used to define DSSI countries
                                            under greater potential Chinese debt stress according to China’s dominance
                                            in bilateral and external payments due in 2020. The thresholds (dotted
                                            lines) indicate the median of all DSSI countries. This graph aligns directly
                                            with the Debt Stress 2 map.
                                        </li>
                                    </ul>
                                    You can generate the same scatterplots in our original publication by selecting the
                                    same variables listed under the Debt Exposure 1 and Debt Exposure 2 maps (above) for
                                    the x- and y-axes.
                                </Paragraph>
                                <Box direction="row" justify="center">
                                    <Button onClick={() => props.changeModule("cde")}
                                            label="Open China Debt Stress Module"/>
                                </Box>
                                <Heading level={5}><u>Environmental Threats</u></Heading>

                                <Paragraph fill size="small">
                                    This page includes environmental data used to assess climate and nature threats
                                    within countries. Climate threats include national carbon emissions and
                                    vulnerability to climate change. Nature threats include national tree cover loss and
                                    threatened species density.
                                </Paragraph>

                                <Paragraph fill size="small">
                                    In addition to individual variables, you can choose the following maps to generate
                                    the figures included in our original publication:
                                    <ul>
                                        <li>
                                            Climate Threats: This map shows the degree of both climate threats present
                                            within each country.
                                        </li>
                                        <li>
                                            Nature Threats: This map shows the degree of both nature threats present
                                            within each country.
                                        </li>
                                    </ul>
                                </Paragraph>

                                <Paragraph fill size="small">
                                    You can generate the same scatterplots in our original publication by selecting the
                                    same variables listed under the Climate Threats and Nature Threats maps (above) for
                                    the x- and y-axes.
                                </Paragraph>
                                <Box direction="row" justify="center">
                                    <Button onClick={() => props.changeModule("es")}
                                            label="Open Environmental Threats Module"/>
                                </Box>

                                <Heading level={5}><u>Opportunities</u></Heading>

                                <Paragraph fill size="small">
                                    This page includes data used to assess where opportunities to implement
                                    debt-for-nature and debt-for-climate swaps may be greatest. Opportunities for
                                    debt-for-nature swaps are measured by the proportion of remaining intact land that
                                    is currently unprotected. Opportunities for debt-for-climate swaps are measured by
                                    the costs of countries’ nationally determined contributions (NDCs) for renewable
                                    energy.
                                </Paragraph>

                                <Paragraph fill size="small">
                                    In the mapping tool, you can select whether to map the debt-for-climate (Climate) or
                                    debt-for-nature (Nature) variables.
                                </Paragraph>

                                <Paragraph fill size="small">
                                    In the graphing tool, you can select these opportunity variables as well as
                                    variables from Environmental Threats to explore relationships between opportunities
                                    and threats.
                                </Paragraph>
                                <Box direction="row" justify="center">
                                    <Button onClick={() => props.changeModule("opp")}
                                            label="Open Opportunities Module"/>
                                </Box>

                                <Heading level={5}><u>Data</u></Heading>
                                <Paragraph fill size="small">
                                    On the Data page, you can view a description of all the data included in the
                                    interactive maps and graphs, as well as the original source(s) of the data.

                                    Users should be mindful to cite the original data sources when discussing or
                                    incorporating the data into their own work. Any references to the analytic outputs
                                    of this project (e.g. debt stressed countries, the global outlook of debt swap
                                    potential) should cite: B. A. Simmons, R. Ray, H. Yang, K. P. Gallagher (2021) China
                                    can helps solve the debt and environmental crises. Science 371: 468-470.
                                </Paragraph>
                                <Box direction="row" justify="center">
                                    <Button onClick={() => props.changeModule("data")} label="Go to Data Tab"/>
                                </Box>
                            </Box>
                        </Tab>
                    </Tabs>
                </Box>
                <Box pad={{top: "small"}}>
                    <Text size="xsmall" weight="bold">When citing: B. A. Simmons, R. Ray, H. Yang,
                    K. P. Gallagher (2021) China can help solve the debt and environmental crises. Science 371:
                    468-470.</Text>
                    <Anchor href="mailto:mheydt@bu.edu"
                            label={<Text color="blue" size="small"><u>Click here to Send Feedback or Support Requests.</u></Text>}/>
                </Box>
            </Box>
        </Box>
    );
}
